import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { name: "", whatsapp: "", code: "", commission_type: "default", commission_value: "", friend_discount: "", note: "" };

// editing: null (closed) | {} (new) | referral doc
export const ReferralCodeDialog = ({ editing, onClose, onSaved, defaults }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const isEdit = !!editing?.id;
  useEffect(() => {
    if (!editing) return;
    setF({ ...EMPTY, ...editing, commission_type: editing.commission_type || "default", commission_value: editing.commission_value ?? "", friend_discount: editing.friend_discount ?? "", note: editing.note || "" });
  }, [editing]);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async () => {
    setBusy(true);
    const body = { name: f.name, whatsapp: f.whatsapp, code: f.code, note: f.note, commission_type: f.commission_type,
      commission_value: f.commission_type === "default" ? null : Number(f.commission_value || 0), friend_discount: f.friend_discount === "" ? null : Number(f.friend_discount) };
    try {
      const { data } = isEdit ? await api.put(`/admin/referrals/${editing.id}`, body) : await api.post("/admin/referrals", body);
      toast.success(isEdit ? `${data.code} updated` : `Code ${data.code} created & approved`);
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="referral-code-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">{isEdit ? `Edit ${editing.code}` : "Create referral code"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Partner / referrer name</Label><Input value={f.name} onChange={set("name")} placeholder="e.g. Rahul (Instagram)" data-testid="ref-name" /></div>
            <div><Label>WhatsApp</Label><Input value={f.whatsapp} onChange={set("whatsapp")} placeholder="91XXXXXXXXXX" data-testid="ref-whatsapp" /></div>
          </div>
          {!isEdit && <div><Label>Custom code <span className="text-muted-foreground font-normal">(blank = auto)</span></Label><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder="RAHUL10" className="font-mono uppercase" data-testid="ref-code" /></div>}
          <div className="rounded-xl border p-3 space-y-3 bg-muted/30">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Commission to referrer (per completed trip)</Label>
            <div className="grid grid-cols-2 gap-3">
              <Select value={f.commission_type} onValueChange={set("commission_type")}>
                <SelectTrigger data-testid="ref-commission-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default (Settings · ₹{defaults?.referral_reward ?? 200})</SelectItem>
                  <SelectItem value="fixed">Fixed ₹ per booking</SelectItem>
                  <SelectItem value="percent">% of booking value</SelectItem>
                </SelectContent>
              </Select>
              {f.commission_type !== "default" && <Input type="number" min={0} value={f.commission_value} onChange={set("commission_value")} placeholder={f.commission_type === "percent" ? "e.g. 5" : "e.g. 500"} data-testid="ref-commission-value" />}
            </div>
            <div><Label>Friend's discount ₹ <span className="text-muted-foreground font-normal">(blank = Settings · ₹{defaults?.referral_friend_discount ?? 500})</span></Label><Input type="number" min={0} value={f.friend_discount} onChange={set("friend_discount")} data-testid="ref-friend-discount" /></div>
          </div>
          <div><Label>Internal note</Label><Input value={f.note} onChange={set("note")} placeholder="Influencer deal, agency partner…" data-testid="ref-note" /></div>
          <Button className="w-full rounded-full" onClick={save} disabled={busy || !f.name || !f.whatsapp} data-testid="ref-save">{isEdit ? "Save changes" : "Create & approve"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
